import { useState } from "react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import CallToAction from "../components/CallToAction";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// Define gallery categories and images
const categories = [
  {
    id: "all",
    name: "Vse slike",
  },
  {
    id: "face",
    name: "Obraz",
  },
  {
    id: "body",
    name: "Telo",
  },
  {
    id: "hands",
    name: "Roke",
  }
];

const galleryImages = [
  {
    id: 1,
    title: "Korekcija vek - Pred",
    imageUrl: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "face",
    beforeAfter: "before",
    pairId: "eyelid-1"
  },
  {
    id: 2,
    title: "Korekcija vek - Po",
    imageUrl: "https://images.unsplash.com/photo-1549351512-c5e12b11e283?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "face",
    beforeAfter: "after",
    pairId: "eyelid-1"
  },
  {
    id: 3,
    title: "Povečanje prsi - Pred",
    imageUrl: "https://images.unsplash.com/photo-1569078449082-d264d9e239c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "body",
    beforeAfter: "before",
    pairId: "breast-1"
  },
  {
    id: 4,
    title: "Povečanje prsi - Po",
    imageUrl: "https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "body",
    beforeAfter: "after",
    pairId: "breast-1"
  },
  {
    id: 5,
    title: "Abdominoplastika - Pred",
    imageUrl: "https://images.unsplash.com/photo-1574279606130-09958dc756f7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "body",
    beforeAfter: "before",
    pairId: "tummy-1"
  },
  {
    id: 6,
    title: "Abdominoplastika - Po",
    imageUrl: "https://images.unsplash.com/photo-1532466255339-9d5a715a69e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "body",
    beforeAfter: "after",
    pairId: "tummy-1"
  },
  {
    id: 7,
    title: "Lifting obraza - Pred",
    imageUrl: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "face",
    beforeAfter: "before",
    pairId: "facelift-1"
  },
  {
    id: 8,
    title: "Lifting obraza - Po",
    imageUrl: "https://images.unsplash.com/photo-1554151228-14d9def656e4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "face",
    beforeAfter: "after",
    pairId: "facelift-1"
  },
  {
    id: 9,
    title: "Rekonstrukcija roke - Pred",
    imageUrl: "https://images.unsplash.com/photo-1506634064465-7dab4de896ed?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "hands",
    beforeAfter: "before",
    pairId: "hand-1"
  },
  {
    id: 10,
    title: "Rekonstrukcija roke - Po",
    imageUrl: "https://images.unsplash.com/photo-1513463551159-e921066e144e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "hands",
    beforeAfter: "after",
    pairId: "hand-1"
  },
  {
    id: 11,
    title: "Pomladitev obraza - Pred",
    imageUrl: "https://images.unsplash.com/photo-1600334129128-685c5582fd35?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "face",
    beforeAfter: "before",
    pairId: "face-rejuv-1"
  },
  {
    id: 12,
    title: "Pomladitev obraza - Po",
    imageUrl: "https://images.unsplash.com/photo-1596815064285-45ed8a9c0463?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "face",
    beforeAfter: "after",
    pairId: "face-rejuv-1"
  }
];

// Organize images into pairs
const createGalleryPairs = (images: typeof galleryImages) => {
  const pairs: Record<string, { before: typeof galleryImages[0]; after: typeof galleryImages[0] }> = {};
  
  images.forEach(image => {
    if (!pairs[image.pairId]) {
      pairs[image.pairId] = { before: null as any, after: null as any };
    }
    
    if (image.beforeAfter === "before") {
      pairs[image.pairId].before = image;
    } else {
      pairs[image.pairId].after = image;
    }
  });
  
  return Object.values(pairs);
};

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedImage, setSelectedImage] = useState<null | {
    imageUrl: string;
    title: string;
    index: number;
  }>(null);
  
  const galleryPairs = createGalleryPairs(galleryImages);
  
  const filteredPairs = activeCategory === "all" 
    ? galleryPairs 
    : galleryPairs.filter(pair => pair.before.category === activeCategory);
  
  const flattenedImages = galleryImages.map((img) => ({
    imageUrl: img.imageUrl,
    title: img.title
  }));
  
  const handleLightboxPrev = () => {
    if (selectedImage && selectedImage.index > 0) {
      setSelectedImage({
        ...flattenedImages[selectedImage.index - 1],
        index: selectedImage.index - 1
      });
    }
  };
  
  const handleLightboxNext = () => {
    if (selectedImage && selectedImage.index < flattenedImages.length - 1) {
      setSelectedImage({
        ...flattenedImages[selectedImage.index + 1],
        index: selectedImage.index + 1
      });
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Galerija"
        description="Oglejte si rezultate naših estetskih in rekonstruktivnih posegov."
      />
      
      {/* Categories Filter */}
      <section className="bg-white py-12">
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <p className="text-gray-700">
              Spodaj si lahko ogledate primere naših posegov in njihovih rezultatov. Za več informacij o 
              posameznih posegih vas vabimo, da si ogledate predstavitve storitev ali nas kontaktirate.
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3 justify-center mb-8">
            {categories.map(category => (
              <Button 
                key={category.id}
                variant={activeCategory === category.id ? "default" : "outline"}
                className={`rounded-full ${activeCategory === category.id ? 'bg-medical-500' : 'border-medical-300'}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Gallery Grid */}
      <section className="bg-gray-50 py-12 md:py-16">
        <div className="container mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPairs.map((pair, index) => (
              <div key={pair.before.pairId} className="glass-card p-6 overflow-hidden">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {pair.before.title.split(" - ")[0]}
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <Dialog>
                    <DialogTrigger asChild>
                      <button 
                        className="block w-full"
                        onClick={() => setSelectedImage({
                          imageUrl: pair.before.imageUrl,
                          title: pair.before.title,
                          index: flattenedImages.findIndex(img => img.imageUrl === pair.before.imageUrl)
                        })}
                      >
                        <div className="relative rounded-2xl overflow-hidden group">
                          <img 
                            src={pair.before.imageUrl} 
                            alt={pair.before.title}
                            className="w-full h-40 object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="text-white font-medium">Pred</span>
                          </div>
                        </div>
                      </button>
                    </DialogTrigger>
                  </Dialog>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <button 
                        className="block w-full"
                        onClick={() => setSelectedImage({
                          imageUrl: pair.after.imageUrl,
                          title: pair.after.title,
                          index: flattenedImages.findIndex(img => img.imageUrl === pair.after.imageUrl)
                        })}
                      >
                        <div className="relative rounded-2xl overflow-hidden group">
                          <img 
                            src={pair.after.imageUrl} 
                            alt={pair.after.title}
                            className="w-full h-40 object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="text-white font-medium">Po</span>
                          </div>
                        </div>
                      </button>
                    </DialogTrigger>
                  </Dialog>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Image Lightbox */}
      {selectedImage && (
        <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
          <DialogContent className="max-w-4xl bg-black/90 border-none">
            <div className="relative w-full">
              <button 
                className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 z-10"
                onClick={() => setSelectedImage(null)}
              >
                <X size={20} />
              </button>
              
              <div className="w-full max-h-[80vh] flex items-center justify-center">
                <img 
                  src={selectedImage.imageUrl} 
                  alt={selectedImage.title}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              </div>
              
              <div className="absolute top-1/2 left-4 transform -translate-y-1/2">
                <button
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
                  onClick={handleLightboxPrev}
                  disabled={selectedImage.index === 0}
                >
                  <ChevronLeft size={24} />
                </button>
              </div>
              
              <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
                <button
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
                  onClick={handleLightboxNext}
                  disabled={selectedImage.index === flattenedImages.length - 1}
                >
                  <ChevronRight size={24} />
                </button>
              </div>
              
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-white text-lg font-medium">{selectedImage.title}</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      <CallToAction 
        title="Želite izvedeti več?"
        subtitle="Kontaktirajte nas za brezplačen posvet in začnite svoje potovanje do željenega videza."
      />
    </Layout>
  );
};

export default GalleryPage;